import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { NameValueDto } from '@ig-core/interfaces/name-value-dto';
import { ReferenceCodeService } from '@app/admin/reference-code/reference-code.service';
import { ApplicationService } from '@app/applications/application.service';
import { LoanApplication, ApplicationAccessSpecifier } from '@app/applications/applications.model';
import { ValidationService } from '../../../application-validators/validation.service'
import { MatSnackBar } from '@angular/material';
import { FolderFilesComposite } from '@app/applications/application-details/loanInformation/loan-folder-files/loan-folder-files.model';
import * as fileSaver from 'file-saver';
import { ImagePreviewDialogService } from '@app/utils/image-preview-dialog/image-preview-dialog.service';
import { IgFileService } from '@ig-core/form/igFile.service';
import { FileUploadService } from '@app/utils/file-upload/file-upload.service';
import { ConfirmationDialogService } from '@app/utils/confirmation-dialog/confirmation-dialog.service';
import { ControlValidators } from '@app/applications/application-validators/control-validators.directive';
@Component({
  selector: 'eng-business-assessment',
  templateUrl: 'business-assessment.template.html',
  styleUrls: ['../../application-details.styles.scss', './business-assessment.scss']
})
export class BusinessAssessmentComponent implements OnInit {

  @Input()
  application: LoanApplication;
  @Input()
  menuCode: string;

  businessAssessmentCompositeDto: any = {};
  businessAssessmentForm: FormGroup;

  isFormDisabled: boolean;
  allowAccess: boolean;
  menuItemAllowAccess: boolean;

  allFilesInFolder: FolderFilesComposite;
  applicationAccessSpecifiers: ApplicationAccessSpecifier[];
  menuItemAccessSpecifier: ApplicationAccessSpecifier;

  businessNatures: NameValueDto[];
  ownershipTypes: NameValueDto[];
  businessOperationFiles: FolderFilesComposite;
  businessPlaceVerificationFiles: FolderFilesComposite;
  constructor(private formBuilder: FormBuilder,
    private referenceCodeService: ReferenceCodeService,
    private applicationService: ApplicationService,
    private _snackbar: MatSnackBar,
    private validationService: ValidationService,
    private confirmationDialogService: ConfirmationDialogService,
    private imagePreviewDialogService: ImagePreviewDialogService,
    private fileService: IgFileService,
    private fileUploadService: FileUploadService) {
    this.businessAssessmentForm = this.formBuilder.group({
      natureOfBusiness: [''],
      currentStockLevels: [''],
      appearanceOfStock: [''],
      seasonalityInBusiness: [''],
      dailyTurnoverVolume: [''],
      marginPercent: [''],
      assessedMarginPercent: [''],
      monthlyProfit: [''],
      assetsPurchases: [''],
      remarks: [''],

      landmarkConfirmation: [''],
      distanceFromBranch: [''],
      yearsInBusiness: [''],
      ownershipType: [''],
      ownerName: [''],
      ownerPhoneNumber: [''],
      rentPaidAmount: [''],
      constructionType: [''],
      buildingArea: [''],
      assetsObserved: [''],
      verificationRemarks: [''],
      documentsObserved: [''],
    });
    this.allowAccess = this.applicationService.allowAccess;
  }

  ngOnInit() {
    this.isFormDisabled = true;
    this.applyValidatorsToBusinessDetails();
    this.getRefCodes();
    this.fetchBusinessAssessment();
    this.getMenuItemAccess();
  }

  applyValidatorsToBusinessDetails() {
    this.validationService.applyValidationRules(this.businessAssessmentForm, "BusinessAssessmentDetails").then((controlValidators) => {
    }).catch(() => {
    })
  }

  getRefCodes() {
    this.referenceCodeService.getShortRefCodes('premise_ownership').subscribe((response: any) => {
      this.ownershipTypes = response.premise_ownership;
    });
    this.referenceCodeService.getShortRefCodes('business_nature').subscribe((response: any) => {
      this.businessNatures = response.business_nature;
    });
  }

  fetchBusinessAssessment() {
    this.applicationService.getBusinessAssessment(this.application.uid).subscribe((response: any) => {
      if (response.body && response.body.businessAssessment) {
        this.businessAssessmentCompositeDto = response.body;
        this.buildBusinessInfoForm(response.body.businessAssessment);
      }
    });
  }

  buildBusinessInfoForm(businessAssessment?: any) {
    const operationVerification =
      businessAssessment.businessOperationVerification || {};

    const placeVerification =
      businessAssessment.businessPlaceVerification || {};

    const operationData =
      operationVerification.businessOperationVerificationAssessmentData || {};

    const placeData =
      placeVerification.businessPlaceVerificationAssessmentData || {};

    this.businessAssessmentForm.patchValue({
      // BUSINESS OPERATION VERIFICATION
      natureOfBusiness: operationData.natureOfBusiness,
      currentStockLevels: operationData.currentStockLevels,
      appearanceOfStock: operationData.appearanceOfStock,
      seasonalityInBusiness: operationData.seasonalityInBusiness,
      dailyTurnoverVolume: operationData.dailyTurnoverVolume,
      marginPercent: operationData.marginPercent,
      assessedMarginPercent: operationData.assessedMarginPercent,
      monthlyProfit: operationData.monthlyProfit,
      assetsPurchases: operationData.assetsPurchases,
      remarks: operationData.remarks,

      // BUSINESS PLACE VERIFICATION
      landmarkConfirmation: placeData.landmarkConfirmation,
      distanceFromBranch: placeData.distanceFromBranch,
      yearsInBusiness: placeData.yearsInBusiness,
      ownershipType: placeData.ownershipType,
      ownerName: placeData.ownerName,
      ownerPhoneNumber: placeData.ownerPhoneNumber,
      rentPaidAmount: placeData.rentPaidAmount,
      constructionType: placeData.constructionType,
      buildingArea: placeData.buildingArea,
      assetsObserved: placeData.assetsObserved,
      verificationRemarks: placeData.remarks,
      documentsObserved: placeData.documentsObserved
    });
    this.businessAssessmentForm.disable();
    this.getSelectedFolderFiles(operationVerification.fileFolderUid, 'BUSINESS_OPERATION');
    this.getSelectedFolderFiles(placeVerification.fileFolderUid, 'BUSINESS_PLACE');
  }

  saveBusinessDetails() {
    this.validationService.markFormGroupTouched(this.businessAssessmentForm);

    if (!this.isFormDisabled && this.businessAssessmentForm.valid) {

      const formValue = this.businessAssessmentForm.getRawValue();

      if (!this.businessAssessmentCompositeDto) {
        this.businessAssessmentCompositeDto = {};
      }

      if (!this.businessAssessmentCompositeDto.businessAssessment) {
        this.businessAssessmentCompositeDto.businessAssessment = {};
      }

      const businessAssessment = this.businessAssessmentCompositeDto.businessAssessment;

      if (!businessAssessment.businessOperationVerification) {
        businessAssessment.businessOperationVerification = {};
      }

      const operationVerification = businessAssessment.businessOperationVerification;

      // Set only for CREATE
      if (!operationVerification.uid) {
        operationVerification.category = 'BUSINESS_OPERATION_VERIFICATION';
        operationVerification.contextUid = this.application.uid;
        operationVerification.contextType = 'application';
      }

      operationVerification.businessOperationVerificationAssessmentData = {
        natureOfBusiness: formValue.natureOfBusiness,
        currentStockLevels: formValue.currentStockLevels,
        appearanceOfStock: formValue.appearanceOfStock,
        seasonalityInBusiness: formValue.seasonalityInBusiness,
        dailyTurnoverVolume: formValue.dailyTurnoverVolume,
        marginPercent: formValue.marginPercent,
        assessedMarginPercent: formValue.assessedMarginPercent,
        monthlyProfit: formValue.monthlyProfit,
        assetsPurchases: formValue.assetsPurchases,
        remarks: formValue.remarks
      };

      if (!businessAssessment.businessPlaceVerification) {
        businessAssessment.businessPlaceVerification = {};
      }

      const placeVerification = businessAssessment.businessPlaceVerification;

      // Set only for CREATE
      if (!placeVerification.uid) {
        placeVerification.category = 'BUSINESS_PLACE_VERIFICATION';
        placeVerification.contextUid = this.application.uid;
        placeVerification.contextType = 'application';
      }

      placeVerification.businessPlaceVerificationAssessmentData = {
        landmarkConfirmation: formValue.landmarkConfirmation,
        distanceFromBranch: formValue.distanceFromBranch,
        yearsInBusiness: formValue.yearsInBusiness,
        ownershipType: formValue.ownershipType,
        ownerName: formValue.ownerName,
        ownerPhoneNumber: formValue.ownerPhoneNumber,
        rentPaidAmount: formValue.rentPaidAmount,
        constructionType: formValue.constructionType,
        buildingArea: formValue.buildingArea,
        assetsObserved: formValue.assetsObserved,
        remarks: formValue.verificationRemarks,
        documentsObserved: formValue.documentsObserved
      };

      const payload = {
        businessAssessment: businessAssessment
      };

      this.applicationService.saveBusinessAssessment(this.application.uid, payload).toPromise().then(
        (_success) => {
          this._snackbar.open("Business updated successfully", "Close", {
            duration: 2000,
          });
          this.isFormDisabled = true;
          this.fetchBusinessAssessment();
          this.businessAssessmentForm.disable();
        }, (failure) => {
          let errormesg = []
          errormesg.push(failure)
          this.applicationService.displayErrorMessages(errormesg);
        }
      );
    }
  }

  enableDisableForm() {
    this.businessAssessmentForm.enable();
    this.onOwnershipTypeChange(this.businessAssessmentForm.value.ownershipType);
    this.isFormDisabled = false;
  }

  cancelForm() {
    this.isFormDisabled = true;
    this.buildBusinessInfoForm(this.businessAssessmentCompositeDto.businessAssessment);
    this.businessAssessmentForm.disable();
  }

  getSelectedFolderFiles(folderUid, fileType: 'BUSINESS_OPERATION' | 'BUSINESS_PLACE') {
    this.applicationService.getAllFilesInApplicationFolder(this.application.uid, folderUid).subscribe((response: any) => {
      if (response) {
        this.allFilesInFolder = response.body;
        this.allFilesInFolder.fileInfo.forEach(element => {
          if (element.fileSize) {
            element["convertedFileSize"] = this.applicationService.bytesToSize(element.fileSize)
          }
        });
        if (fileType === 'BUSINESS_OPERATION') {
          this.businessOperationFiles = this.allFilesInFolder;
        } else {
          this.businessPlaceVerificationFiles = this.allFilesInFolder;
        }
      }
    });
  }

  openImageFileUploadDialog(type: 'BUSINESS_OPERATION' | 'BUSINESS_PLACE') {
    const folderUid =
      type === 'BUSINESS_OPERATION'
        ? this.businessAssessmentCompositeDto.businessAssessment.businessOperationVerification.fileFolderUid
        : this.businessAssessmentCompositeDto.businessAssessment.businessPlaceVerification.fileFolderUid;

    const files =
      type === 'BUSINESS_PLACE'
        ? this.businessOperationFiles.fileInfo
        : this.businessPlaceVerificationFiles.fileInfo;
    this.fileUploadService.open({ "uploadType": "multiple", "folderUid": folderUid, "applicationUid": this.application.uid, "files": files })
    this.fileUploadService.confirmed().subscribe(data => {
      this.getSelectedFolderFiles(data.folderUid, type)
    })
  }

  openfileDeleteConfirmation(selectedFile, fileType: 'BUSINESS_OPERATION' | 'BUSINESS_PLACE') {
    this.confirmationDialogService.open({ "btnCancelText": "Cancel", "btnConfirmText": "Delete", "message": "Are you sure you want to delete this File?" })
    this.confirmationDialogService.confirmed().subscribe(data => {
      if (data) {
        this.deleteFile(selectedFile, fileType)
      }
    })
  }

  deleteFile(selectedFile, fileType) {
    this.applicationService.fileDelete(this.application.uid, selectedFile.uid).subscribe((response) => {
      if (response.body.status == "success") {
        this._snackbar.open("File deleted successfully", "Close", {
          duration: 2000,
        });
        if (fileType === 'BUSINESS_OPERATION') {
          this.getSelectedFolderFiles(
            this.businessAssessmentCompositeDto.businessAssessment.businessOperationVerification.fileFolderUid,
            'BUSINESS_OPERATION'
          );
        } else {
          this.getSelectedFolderFiles(
            this.businessAssessmentCompositeDto.businessAssessment.businessPlaceVerification.fileFolderUid,
            'BUSINESS_PLACE'
          );
        }
      } else {
        let errormesg = []
        errormesg.push("File deleted failed")
        errormesg.push(response.body.message)
        this.applicationService.displayErrorMessages(errormesg);
      }
    }, (failure) => {
      let errormesg = []
      errormesg.push("File deleted failed")
      errormesg.push(failure)
      this.applicationService.displayErrorMessages(errormesg);
    });
  }

  downloadFile(file) {
    this.applicationService.downloadFileFromApplicationFolder(this.application.uid, file.folderUid, file.fileId).subscribe(image => {
      const blob = new Blob([image.data], { type: file.type });
      fileSaver.saveAs(blob, file.fileName);
      this._snackbar.open("File has been downloaded successfully", "Close", {
        duration: 5000,
      });
    })
  }

  openContentElement(fileType) {
    this.imagePreviewDialogService.open({ "images": fileType === 'BUSINESS_OPERATION' ? this.businessOperationFiles : this.businessPlaceVerificationFiles })
  }

  viewPdf(file) {
    this.fileService.fileStreamById(file.fileId, "false").subscribe(res => {
      window.open(res.data, '_blank');
    })
  }

  //this function will get either this menu item  is editable or readonly 
  getMenuItemAccess() {
    this.applicationService.getApplicationAccessModifiers(this.application.uid)
      .subscribe((response) => {
        this.applicationAccessSpecifiers = response.body;
        this.menuItemAccessSpecifier = this.applicationAccessSpecifiers
          .find(accessSpecifier => accessSpecifier.category === this.menuCode);
        if (this.menuItemAccessSpecifier) {
          this.menuItemAllowAccess = this.menuItemAccessSpecifier.allowAccess;
        }
      });
  }

  onOwnershipTypeChange(value?: string, isDropdownChange: boolean = false) {
    const control = this.businessAssessmentForm.controls.rentPaidAmount;
    // Clear the value when dropdown is changed by user
    if (isDropdownChange) {
      control.setValue(null);
    }
    if (value !== 'RENTD') {
      this.businessAssessmentForm.controls['rentPaidAmount']
        .setValidators([ControlValidators.numberValidator("Rent paid should be a number")]);
    } else {
      this.businessAssessmentForm.controls['rentPaidAmount']
        .setValidators([ControlValidators.requiredValidator("Rent paid amount is required"),
        ControlValidators.numberValidator("Rent paid should be a number")]);
    }
    control.updateValueAndValidity();
  }
}